import { Bodies, Body, Composite, Engine, type IBodyDefinition } from 'matter-js';
import { clamp01 } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import { shatterGlyphs, type ShatterPattern } from '../../core/shatter';
import type { RenderTarget, TechniqueContext } from '../types';
import type { ShatterValues } from './params';

/**
 * Letters break into fragments and fall apart under rigid-body physics.
 *
 * matter-js requires convex bodies; concave polygons are silently decomposed only if
 * poly-decomp is present, and a badly formed body ends up as its convex hull. The
 * Voronoi fragments are convex by construction, so they map onto bodies directly — one
 * reason the break pattern is built from a Voronoi diagram rather than by cutting the
 * outline.
 *
 * Fragments below a few square pixels are dropped rather than simulated: matter-js
 * grows unstable with degenerate bodies, and they are invisible anyway.
 */
export interface ShatterState {
  engine: Engine;
  bodies: Body[];
  /** Local vertices per body, so drawing does not depend on matter's internals. */
  shapes: Float32Array[];
  tint: Float32Array;
  palette: ResolvedPalette;
  stepsDone: number;
  targetSteps: number;
  maxSpeed: number;
}

const MIN_AREA = 6;

function values(ctx: TechniqueContext): ShatterValues {
  return ctx.values as unknown as ShatterValues;
}

export function init(ctx: TechniqueContext): ShatterState {
  const v = values(ctx);
  const engine = Engine.create();
  engine.gravity.y = v.gravity;
  engine.gravity.x = 0;

  const fragments = shatterGlyphs(ctx.field.glyphs, v.fragments, v.pattern as ShatterPattern, ctx.rng);
  const bodies: Body[] = [];
  const shapes: Float32Array[] = [];
  const tints: number[] = [];

  const originX = v.origin.x * ctx.width;
  const originY = v.origin.y * ctx.height;

  for (const fragment of fragments) {
    if (fragment.area < MIN_AREA || fragment.points.length < 6) continue;

    const vertices = [];
    for (let i = 0; i < fragment.points.length; i += 2) {
      vertices.push({ x: fragment.points[i], y: fragment.points[i + 1] });
    }

    const options: IBodyDefinition = {
      restitution: v.restitution,
      friction: v.friction,
      frictionAir: 0.012,
    };
    const body = Bodies.fromVertices(fragment.centroidX, fragment.centroidY, [vertices], options, true);
    if (!body) continue;

    // Store the shape in body-local coordinates once, so `draw` only needs the body's
    // position and angle.
    const local = new Float32Array(fragment.points.length);
    for (let i = 0; i < fragment.points.length; i += 2) {
      local[i] = fragment.points[i] - fragment.centroidX;
      local[i + 1] = fragment.points[i + 1] - fragment.centroidY;
    }

    if (v.explosion > 0) {
      const dx = fragment.centroidX - originX;
      const dy = fragment.centroidY - originY;
      const distance = Math.hypot(dx, dy) || 1;
      // Falls off with distance, so the blast reads as coming from a point.
      const strength = (v.explosion * body.mass) / (1 + distance / 120);
      Body.applyForce(body, body.position, {
        x: (dx / distance) * strength * 0.0016,
        y: (dy / distance) * strength * 0.0016,
      });
    }
    Body.setAngularVelocity(body, ctx.rng.signed(v.spin));

    bodies.push(body);
    shapes.push(local);
    tints.push(ctx.rng.next());
  }

  Composite.add(engine.world, bodies);

  if (v.walls) {
    const t = 200;
    Composite.add(engine.world, [
      Bodies.rectangle(ctx.width / 2, ctx.height + t / 2, ctx.width * 3, t, { isStatic: true }),
      Bodies.rectangle(-t / 2, ctx.height / 2, t, ctx.height * 3, { isStatic: true }),
      Bodies.rectangle(ctx.width + t / 2, ctx.height / 2, t, ctx.height * 3, { isStatic: true }),
    ]);
  }

  return {
    engine,
    bodies,
    shapes,
    tint: Float32Array.from(tints),
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    stepsDone: 0,
    targetSteps: v.steps,
    maxSpeed: 1,
  };
}

export function step(state: ShatterState, _dt: number, ctx: TechniqueContext): ShatterState {
  if (state.stepsDone >= state.targetSteps) return state;

  const perFrame = Math.max(1, Math.round(3 * clamp01(ctx.quality)));
  for (let i = 0; i < perFrame && state.stepsDone < state.targetSteps; i++) {
    // Fixed timestep: matter-js is not stable with a variable one, and a fixed step is
    // also what makes the result reproducible from the seed.
    Engine.update(state.engine, 1000 / 60);
    state.stepsDone++;
  }

  let max = 1;
  for (const body of state.bodies) {
    const speed = Math.hypot(body.velocity.x, body.velocity.y);
    if (speed > max) max = speed;
  }
  state.maxSpeed = max;
  return state;
}

export function isComplete(state: ShatterState): boolean {
  return state.stepsDone >= state.targetSteps;
}

export function draw(state: ShatterState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  c.lineWidth = v.lineWidth;
  c.lineJoin = 'round';

  for (let i = 0; i < state.bodies.length; i++) {
    const body = state.bodies[i];
    const local = state.shapes[i];
    if (local.length < 6) continue;

    const cos = Math.cos(body.angle);
    const sin = Math.sin(body.angle);
    const path = new Path2D();
    for (let k = 0; k < local.length; k += 2) {
      const x = body.position.x + local[k] * cos - local[k + 1] * sin;
      const y = body.position.y + local[k] * sin + local[k + 1] * cos;
      if (k === 0) path.moveTo(x, y);
      else path.lineTo(x, y);
    }
    path.closePath();

    let t: number;
    switch (v.colorMode) {
      case 'speed':
        t = clamp01(Math.hypot(body.velocity.x, body.velocity.y) / state.maxSpeed);
        break;
      case 'height':
        t = clamp01(body.position.y / ctx.height);
        break;
      case 'random':
        t = state.tint[i];
        break;
      default:
        t = 0;
    }
    const color = v.colorMode === 'solid' ? state.palette.stop(0, v.opacity) : state.palette.at(t, v.opacity);

    if (v.style === 'fill' || v.style === 'both') {
      c.fillStyle = color;
      c.fill(path);
    }
    if (v.style === 'stroke' || v.style === 'both') {
      c.strokeStyle = color;
      c.stroke(path);
    }
  }
}

export function elementCount(state: ShatterState): number {
  return state.bodies.length;
}
